import { useApolloClient } from '@apollo/client';
import { useI18n } from '@i18n';
import isNil from 'lodash/isNil';
import { useSnackbar } from 'notistack';
import { useEffect, useRef } from 'react';
import { SnackbarTimeouts } from '@/components/NotistackProvider';
import { DUPLICATE_SCENARIO_SUBSCRIPTION } from '@/graphql/ActionPlanning.queries';
import useSubscriptionFallback from '@/hooks/useSubscriptionFallback';

export const useSubscriptionDuplicateScenario = ({
  scenarioId,
  scenarioName,
  isWatched,
  onFinish,
}: {
  scenarioId: number;
  scenarioName: string;
  isWatched: boolean;
  onFinish: VoidFunction;
}) => {
  const client = useApolloClient();
  const subscriptionDuplicateScenario = useSubscriptionFallback(DUPLICATE_SCENARIO_SUBSCRIPTION, {
    skip: !isWatched,
    skipCondition: (data) => !isNil(data.scenario_by_pk) && !data.scenario_by_pk.duplication_in_progress,
    variables: { scenarioId },
  });

  const isDuplicationInProgress = subscriptionDuplicateScenario.data?.scenario_by_pk?.duplication_in_progress;
  const previousDuplicationState = useRef<boolean | undefined>(undefined);
  /**
   * This hasHandledFinish ref covers the flow where duplication is started in the modal, the user is
   * redirected to the scenarios list, and the job has already completed before the
   * subscription delivers its first result.
   */
  const hasHandledFinish = useRef(false);
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useI18n();

  useEffect(() => {
    if (!isWatched) return;

    const wasInProgress = previousDuplicationState.current;
    previousDuplicationState.current = isDuplicationInProgress;

    // Fire when duplication finished (either transition true->false or already false on first fetch)
    const duplicationFinished = isDuplicationInProgress === false && !hasHandledFinish.current;

    if (duplicationFinished && (wasInProgress || wasInProgress === undefined)) {
      setTimeout(() => {
        // Refetch the scenarios query to show the duplicated scenario
        client.refetchQueries({
          include: ['ActionPlanningScenariosAllScenarios'],
        });

        onFinish();

        enqueueSnackbar(
          t('ActionPlanning_Scenario_DuplicateScenarioDialog_DuplicationFinished', {
            scenarioName,
          }),
          {
            variant: 'success',
            autoHideDuration: SnackbarTimeouts.Success,
          },
        );
        hasHandledFinish.current = true;
      }, 1000);
    }
  }, [isWatched, isDuplicationInProgress, client, enqueueSnackbar, t, onFinish, scenarioName]);
};
